<template>
  <li :id="'comment-' + comment.id" :class="classNames">
    <div class="incident-comment__inner">
      <div class="incident-comment__meta">
        <a :href="`${edit.url}${comment.id}`" v-if="!!edit.url && !! edit.label" class="btn btn--secondary btn--small incident-comment__edit-link"
           target="_blank" rel="noopener noreferrer">
          {{ edit.label }}
          <Icon icon="external" />
          <span class="sr-only">({{ labels.external }})</span>
        </a>
        <h2 v-if="comment.headline" v-html="comment.headline"></h2>
        <time :datetime="formattedDatetime">{{ labels.update }}: {{ formattedTime }}</time>
      </div>

      <div v-if="comment.content" class="incident-comment__content" v-html="comment.content"></div>
      <div v-if="comment.images && comment.images.length > 0" class="incident-comment__images">
        <figure v-for="(image, index) in comment.images" :key="index" v-html="image"></figure>
      </div>

      <footer v-if="!!comment.modified" class="incident-comment__footer">
        <time class="incident-comment__modified-date" :datetime="formattedModifiedDatetime">{{ labels.edited }}: {{ formattedModifiedTime }}</time>
      </footer>
    </div>
  </li>
</template>

<script>
import { computed, defineComponent, inject, ref } from 'vue';
import Icon from "./Icon.vue";

export default defineComponent( {
  name: 'Comment',
  components: {
    Icon,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    edit: {
      type: [ Boolean, Object ],
      default: false,
      required: false,
    },
  },
  setup( props ) {
    const labels = inject( 'labels' );

    const language = ref( document.documentElement.lang || navigator.language );

    const parseDate = ( date ) => {
      if ( ! date ) return null;
      const timestamp = Number( date );
      const dateObject = ! isNaN( timestamp )
          ? new Date( timestamp * 1000 )
          : new Date( date );

      return isNaN( dateObject.getTime() ) ? null : dateObject;
    };

    const dateObject = parseDate( props.comment.datetime );
    const modifiedDateObject = parseDate( props.comment.modified );

    const formatDate = ( dateObj ) => {
      if ( ! dateObj ) return '';

      const formattedDate = new Intl.DateTimeFormat( language.value, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      } ).format( dateObj );

      const formattedTime = new Intl.DateTimeFormat( language.value, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      } ).format( dateObj );

      return `${ formattedDate } - ${ formattedTime }`;
    };

    /**
     * Returns array of unique class names.
     *
     * @type {ComputedRef<string[]>}
     */
    const classNames = computed( () => {
      const classes = [ 'incident-comment' ];
      if ( props.comment.classes ) {
        classes.push( ...props.comment.classes );
      }

      // Filter duplicate classes.
      return classes.filter( ( value, index, self ) => self.indexOf( value ) === index );
    } );

    const formattedDatetime = computed( () => ( dateObject ? dateObject.toISOString() : null ) );
    const formattedModifiedDatetime = computed( () => ( modifiedDateObject ? modifiedDateObject.toISOString() : null ) );

    const formattedTime = formatDate( dateObject );
    const formattedModifiedTime = formatDate( modifiedDateObject );

    return {
      classNames,
      labels,
      formattedDatetime,
      formattedTime,
      formattedModifiedDatetime,
      formattedModifiedTime
    };
  }
} );
</script>
